import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Table from "@mui/joy/Table";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import { BarChart } from "@mui/x-charts/BarChart";
import { AnalyticsFilters, AnalyticsFiltersState } from "../components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
    WatchSignals,
} from "../components/AnalyticsShared";
import { AnalyticsSeriesChart } from "../components/AnalyticsSeriesChart";
import { useAsyncBlock } from "../hooks";
import { bidsBookDepthApi } from "../../bidsApi";
import { BidBookDepthReport } from "../../../../models/bidAnalytics";
import { formatInt, formatMoneyKopecks, gameName, toNum } from "../format";
import { rollupSeries } from "../rollup";
import { Game } from "../../../../models/games";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

export function BookDepthTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;

    const [state, refresh] = useAsyncBlock<BidBookDepthReport>(
        () =>
            bidsBookDepthApi({
                gameId: filters.gameId,
                from: filters.from,
                to: filters.to,
            }),
        isActive,
        [filters.gameId, filters.from, filters.to]
    );

    const data = state.data?.totals;
    const chartData = React.useMemo(() => {
        const rolled = rollupSeries(state.data?.series ?? [], filters.granularity, {
            sumKeys: [
                "openBidsCount",
                "remainingQty",
                "reservedQty",
                "openGmv",
                "executableBidsCount",
                "executableRemainingQty",
                "executableOpenGmv",
            ],
        });
        return rolled.map((p) => ({
            ...p,
            openGmvRub: toNum(p.openGmv) / 100,
            executableOpenGmvRub: toNum(p.executableOpenGmv) / 100,
        }));
    }, [state.data?.series, filters.granularity]);

    const isEmpty = data != null && data.openBidsCount === 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showPeriod
                showGranularity
            />

            <AnalyticsCard
                title="Сколько спроса открыто прямо сейчас"
                subtitle="KPI — снимок всех открытых. График — когда был создан висящий сейчас спрос (по Created)."
                snapshotLabel="now"
                howToRead={
                    <List size="sm" marker="disc">
                        <ListItem>
                            <Typography level="body-sm">
                                Открытые заявки / остаток штук / сумма ₽ — весь «висящий» спрос
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">В резерве — уже в процессе покупки</Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                Исполняемые — включены заявки и хватает баланса. График фильтруется
                                периодом по дате создания открытых заявок.
                            </Typography>
                        </ListItem>
                    </List>
                }
                watchFor={
                    <WatchSignals
                        items={[
                            {
                                signal: "Разрыв «открыто» vs «исполняемо»",
                                growsIsGood: false,
                                ifGrows: "много «мёртвых» заявок (нет баланса / заявки выключены); разберите в рисках.",
                                ifFalls: "почти совпадают — открытый спрос реальный и может исполниться.",
                            },
                            {
                                signal: "Объём в резерве",
                                growsIsGood: false,
                                ifGrows: "покупки зависают в процессе без завершения; смотрите ордера и риски «Застряла в резерве».",
                                ifFalls: "резерв умеренный и быстро сходит — исполнение идёт штатно.",
                            },
                            {
                                signal: "Открытый объём спроса ₽",
                                ifGrows: "растёт вместе с исполняемым — живая нагрузка на рынок; без исполняемого — копится нерабочий спрос.",
                                ifFalls: "открытый спрос сокращается — меньше висящей нагрузки.",
                            },
                        ]}
                    />
                }
            >
                {state.loading && <BlockLoading />}
                {!state.loading && state.error && <BlockError message={state.error} onRetry={refresh} />}
                {!state.loading && !state.error && isEmpty && (
                    <EmptyState hint="Сейчас нет открытых заявок. Смените игру или обновите." />
                )}
                {!state.loading && !state.error && data && !isEmpty && (
                    <>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            <MetricHint label="Открытых заявок" value={formatInt(data.openBidsCount)} />
                            <MetricHint label="Остаток шт." value={formatInt(data.remainingQty)} />
                            <MetricHint label="В резерве" value={formatInt(data.reservedQty)} color="warning" />
                            <MetricHint
                                label="Открытый объём ₽"
                                value={formatMoneyKopecks(data.openGmv)}
                                hint="Объём спроса в деньгах"
                            />
                            <MetricHint
                                label="Исполняемые заявки"
                                value={formatInt(data.executableBidsCount)}
                                color="success"
                            />
                            <MetricHint
                                label="Исполняемый объём ₽"
                                value={formatMoneyKopecks(data.executableOpenGmv)}
                                color="success"
                            />
                        </Box>

                        <Typography level="title-sm">
                            Когда создан висящий спрос (открытые сейчас)
                        </Typography>
                        <AnalyticsSeriesChart
                            dataset={chartData}
                            granularity={filters.granularity}
                            series={[
                                {
                                    dataKey: "openGmvRub",
                                    label: "Открытый объём ₽",
                                    color: "#1976d2",
                                    valueFormatter: (v) =>
                                        v == null
                                            ? "—"
                                            : `${v.toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`,
                                },
                                { dataKey: "remainingQty", label: "Остаток шт.", color: "#f57c00" },
                                {
                                    dataKey: "executableOpenGmvRub",
                                    label: "Исполняемый объём ₽",
                                    color: "#2e7d32",
                                    valueFormatter: (v) =>
                                        v == null
                                            ? "—"
                                            : `${v.toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`,
                                },
                            ]}
                        />

                        {data.byGame?.length > 0 && (
                            <>
                                <BarChart
                                    height={220}
                                    series={[
                                        {
                                            data: data.byGame.map((g) => toNum(g.openGmv) / 100),
                                            label: "Открытый объём ₽",
                                            color: "#1976d2",
                                            valueFormatter: (v) =>
                                                `${(v ?? 0).toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`,
                                        },
                                    ]}
                                    xAxis={[
                                        {
                                            data: data.byGame.map((g) => gameName(g.gameId)),
                                            scaleType: "band",
                                        },
                                    ]}
                                    margin={{ left: 60, right: 20, top: 20, bottom: 40 }}
                                />

                                <ScrollTable>
                                    <Table size="sm" stickyHeader sx={{ minWidth: 640 }}>
                                        <thead>
                                            <tr>
                                                <th>Игра</th>
                                                <th>Заявок</th>
                                                <th>Остаток</th>
                                                <th>Резерв</th>
                                                <th>Объём ₽</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.byGame.map((g) => (
                                                <tr key={g.gameId}>
                                                    <td>{gameName(g.gameId)}</td>
                                                    <td>{formatInt(g.openBidsCount)}</td>
                                                    <td>{formatInt(g.remainingQty)}</td>
                                                    <td>{formatInt(g.reservedQty)}</td>
                                                    <td>{formatMoneyKopecks(g.openGmv)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </ScrollTable>
                            </>
                        )}
                    </>
                )}
            </AnalyticsCard>
        </Box>
    );
}
